import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

function formatApiErrorDetail(detail) {
    if (detail == null) return "Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = not authenticated
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
            setUser(data);
        } catch {
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const login = async (email, password) => {
        try {
            const { data } = await axios.post(
                `${API}/api/auth/login`,
                { email, password },
                { withCredentials: true }
            );
            setUser(data);
            return { success: true };
        } catch (e) {
            return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) };
        }
    };

    const register = async (email, password, name) => {
        try {
            const { data } = await axios.post(
                `${API}/api/auth/register`,
                { email, password, name, role: 'broker' },
                { withCredentials: true }
            );
            setUser(data);
            return { success: true };
        } catch (e) {
            return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) };
        }
    };

    const logout = async () => {
        try {
            await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
        } catch {
            // Ignore logout errors
        }
        setUser(false);
    };

    const value = {
        user,
        loading,
        login,
        register,
        logout,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin',
        checkAuth
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}
